import React from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Popover, Button } from '@/components/common'
import Image from '@/components/contentful/Image'
import { BellNotificationPopover } from '@/components/notifications/BellNotifications'
import { AngelFundingLogo, Profile } from '@/components/svg'
import { useExperiment } from '@/experimentation'
import SegmentHandler from '@/services/analytics/SegmentHandler'
import useAuth from '@/services/hooks/useAuth'
import useHydration from '@/services/hooks/useHydration'
import useSiteAssets from '@/services/hooks/useSiteAssets'
import useWindowSize from '@/services/hooks/useWindowSize'
import UserMenu from '../UserMenu'

interface Props {
  backgroundColor?: 'transparent' | 'white' | 'lightGray'
}

export const Header: React.FC<Props> = ({ backgroundColor = 'lightGray' }) => {
  const { user, startAuth, openModal } = useAuth()
  const router = useRouter()
  const siteAssets = useSiteAssets()
  const [isOAuth2] = useExperiment('ellis_island_oauth2', true)
  const { innerWidth } = useWindowSize()
  const { isHydrated } = useHydration()

  return (
    <header id="header__main">
      <div
        className={classNames(
          'z-[1000] flex w-full flex-col items-start justify-center font-whitney',
          {
            'absolute left-0 top-0 bg-transparent text-core-white':
              backgroundColor === 'transparent',
            'relative bg-core-gray-100': backgroundColor === 'lightGray',
            'relative bg-core-white': backgroundColor === 'white',
          }
        )}
      >
        <div className="flex h-[56px] w-full items-center justify-center md:h-[72px]">
          <Link
            href={'/'}
            aria-label="angel-funding-logo"
            className={classNames({
              'md:absolute md:left-4': true,
            })}
          >
            {siteAssets && siteAssets.logo && isHydrated && (
              <Image
                image={siteAssets.logo}
                height={innerWidth > 767 ? 32 : 26}
                className="h-[26px] md:h-[32px]"
              />
            )}
            {siteAssets && !siteAssets.logo && siteAssets.contentfulName && (
              <AngelFundingLogo
                className="h-[26px] md:h-[32px]"
                fill1={backgroundColor === 'transparent' ? '#fff' : undefined}
                fill2={backgroundColor === 'transparent' ? '#fff' : undefined}
              />
            )}
          </Link>

          {user && (
            <div className="absolute right-4 flex items-center">
              <BellNotificationPopover />

              <Popover
                content={
                  <UserMenu.LoggedIn className="flex w-[280px] flex-col p-3" />
                }
                placement="bottom-end"
                label="User Menu"
                theme="light"
              >
                <Profile className="h-7 w-7" />
              </Popover>
            </div>
          )}

          {/* Explicitly check that auth is null to avoid SSR/hydration issues */}
          {user === null && (
            <div className="absolute right-4 flex items-center">
              <Popover
                content={
                  <UserMenu.LoggedOut className="flex w-full flex-col p-3" />
                }
                placement="bottom-end"
                label="Login or Sign Up"
                className="md:hidden"
                theme="light"
              >
                <Profile className="h-7 w-7" />
              </Popover>

              <div className="hidden md:block">
                <Button
                  variant="text"
                  size="sm"
                  color={backgroundColor === 'transparent' ? 'white' : 'black'}
                  onClick={() => {
                    if (isOAuth2) {
                      startAuth()
                    } else {
                      openModal('login')
                    }

                    SegmentHandler.track('On Tap', {
                      location: router.asPath,
                      section_name: 'header',
                      element_name: 'menu_item',
                      value: 'Login',
                      action_type: 'tap',
                    })
                  }}
                >
                  Log In
                </Button>

                <Button
                  variant="text"
                  size="sm"
                  color={backgroundColor === 'transparent' ? 'white' : 'black'}
                  className="ml-1"
                  onClick={() => {
                    if (isOAuth2) {
                      startAuth()
                    } else {
                      openModal('register')
                    }
                    SegmentHandler.track('On Tap', {
                      location: router.asPath,
                      section_name: 'header',
                      element_name: 'menu_item',
                      value: 'Sign Up',
                      action_type: 'tap',
                    })
                  }}
                >
                  Sign Up
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
